import {
  createFileRoute,
  redirect,
  stripSearchParams,
} from '@tanstack/react-router';
import { FEATURE_FLAG, PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { getSavedFiltersQueryOptions } from '~/data/savedFilter';
import {
  getSupplierRequestsQueryOptions,
  getSupplierResponsiblePersonOptionsQueryOptions,
  getSuppliersOverviewCountsQueryOptions,
  getSuppliersOverviewQueryOptions,
  getSuppliersPriorityQueryOptions,
  SUPPLIER_ACTIVATION_STATUS,
} from '~/data/supplier';
import { userDataQueryOptions } from '~/data/user';

import { SuppliersListVersionRouting } from '~/modules/suppliers';
import { UNIVERSAL_FILTER_TYPES } from '~/modules/universalFilters';

import { i18n } from '~/utils';
import { buildRouteExport } from '~/utils/buildRouteExport';
import { getRouteHead } from '~/utils/routeUtils';

import {
  suppliersSearchDefaults,
  zodSuppliersSearchSchema,
} from './-searchParams';

const title = i18n.t(($) => $.pageTitle, { ns: 'suppliers' });

export const Route = createFileRoute('/(main)/suppliers/')({
  component: SuppliersListVersionRouting,
  head: () => getRouteHead(title),
  loader: async ({ context: { queryClient }, deps }) => {
    const { selectedCompanyId } = deps as {
      selectedCompanyId: string | undefined;
    };
    const userData = await queryClient.ensureQueryData(
      userDataQueryOptions({ includeCompanyInfo: true }),
    );

    // Don't preload the suppliers overview if the feature flag is not enabled
    const hasSupplierOverviewFeatureFlag =
      userData.companyAccount.data.featureFlags[
        FEATURE_FLAG.SUPPLIER_OVERVIEW
      ] === true;
    if (!hasSupplierOverviewFeatureFlag) {
      return;
    }

    const defaultCompanyId = userData.company.id;
    const companyId = selectedCompanyId ?? defaultCompanyId;

    if (!selectedCompanyId && companyId) {
      throw redirect({
        replace: true,
        search: (prev) => ({
          ...prev,
          selectedCompanyId: companyId,
        }),
        to: '/suppliers/',
      });
    }

    const recipientCompanyIds = companyId ? [companyId] : [];

    if (recipientCompanyIds.length > 0) {
      // Prefetch activation status group counts
      void queryClient.prefetchQuery(
        getSuppliersOverviewCountsQueryOptions({
          queryParams: { recipientCompanyIds },
        }),
      );

      // Prefetch activation status groups
      SUPPLIER_ACTIVATION_STATUS.forEach((activationStatus) => {
        void queryClient.prefetchQuery(
          getSuppliersOverviewQueryOptions({
            queryParams: {
              activationStatuses: [activationStatus],
              limit: PAGINATION_PAGE_SIZE_DEFAULT, // required for parity with SuppliersGridGrouped
              offset: 0, // required for parity with SuppliersGridGrouped
              recipientCompanyIds,
            },
          }),
        );
      });

      // Prefetch check if selected company has any favorites
      void queryClient.prefetchQuery(
        getSuppliersOverviewQueryOptions({
          queryParams: { recipientCompanyIds },
        }),
      );

      // Prefetch supplier requests
      void queryClient.prefetchQuery(
        getSupplierRequestsQueryOptions({
          queryParams: {
            companyAccountId: userData.companyAccount.id,
            limit: PAGINATION_PAGE_SIZE_DEFAULT,
            offset: 0,
          },
        }),
      );

      // Prefetch supplier integration priorities
      void queryClient.prefetchQuery(
        getSuppliersPriorityQueryOptions(recipientCompanyIds[0]),
      );

      // Prefetch filter-related data
      void queryClient.prefetchQuery(
        getSavedFiltersQueryOptions({
          queryParams: {
            type: UNIVERSAL_FILTER_TYPES.suppliers,
          },
        }),
      );
      void queryClient.prefetchQuery(
        getSupplierResponsiblePersonOptionsQueryOptions({
          recipientCompanyIds,
        }),
      );
    }
  },
  loaderDeps: ({ search }) => ({
    selectedCompanyId: search.selectedCompanyId,
  }),
  search: {
    middlewares: [stripSearchParams(suppliersSearchDefaults)],
  },
  staticData: {
    AppBar: {
      title,
    },
  },
  validateSearch: zodSuppliersSearchSchema,
});

export const SUPPLIERS = buildRouteExport({ Route });
